import React, { useMemo } from "react";
import { Pressable, StyleProp, StyleSheet, TextStyle, View, ViewStyle } from "react-native";
import { useTheme } from "../hooks/use-theme";
import Text from "./Text";
import Icon, { IconName } from "./Icon";

type IconInfo = {
    name: IconName;
    color?: string;
};

export type ActionButtonProps = {
    text: string;
    textColor?: string;
    textWidth?: "bold" | "medium";
    leftIcon?: IconInfo;
    rightIcon?: IconInfo;
    style?: StyleProp<ViewStyle>;
    textStyle?: StyleProp<TextStyle>;
    numberOfLines?: number;
    onPress?: () => void | Promise<void>;
};

const ActionButton: React.FC<ActionButtonProps> = ({
    text,
    textColor,
    textWidth = "medium",
    leftIcon,
    rightIcon,
    style,
    textStyle,
    numberOfLines = 1,
    onPress,
}) => {
    const { colors } = useTheme();
    const styles = useStyles();
    const [isPressing, setIsPressing] = React.useState(false);

    const onPressLocal = React.useCallback(() => {
        if (isPressing) {
            // do not multi-press
            return;
        }
        const _func = async () => {
            try {
                setIsPressing(true);
                await onPress?.();
            } finally {
                setIsPressing(false);
            }
        };
        void _func();
    }, [isPressing, onPress]);

    return (
        <Pressable onPress={onPressLocal} style={styles.pressable}>
            <View style={[styles.container, style]}>
                {leftIcon && (
                    <View style={styles.leftIconContainer}>
                        <Icon name={leftIcon.name} color={leftIcon.color} size={24} />
                    </View>
                )}
                <View style={styles.textContainer}>
                    <Text
                        style={[
                            styles.text,
                            { color: textColor || colors.text },
                            textWidth == "medium" ? styles.textMedium : styles.textBold,
                            textStyle,
                        ]}
                        numberOfLines={numberOfLines}
                        ellipsizeMode="tail"
                    >
                        {text}
                    </Text>
                </View>
                {rightIcon && (
                    <View style={styles.rightIconContainer}>
                        <Icon name={rightIcon.name} color={rightIcon.color} size={24} />
                    </View>
                )}
            </View>
        </Pressable>
    );
};

const useStyles = () => {
    const { colors, fonts, gridSize, roundness } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                pressable: {
                    alignSelf: "stretch",
                },
                container: {
                    flexDirection: "row",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    borderWidth: 1,
                    borderColor: colors.border,
                    borderRadius: roundness,
                    alignSelf: "stretch",
                    overflow: "hidden",
                    paddingHorizontal: gridSize / 1.5,
                    paddingVertical: gridSize / 2,
                },
                textContainer: {
                    flexGrow: 1,
                    flexShrink: 1,
                },
                leftIconContainer: {
                    marginRight: gridSize / 2,
                },
                rightIconContainer: {
                    marginLeft: gridSize / 2,
                },
                text: {
                    flexGrow: 1,
                },
                textMedium: {
                    ...fonts.bodyMedium,
                },
                textBold: {
                    ...fonts.bodyLarge,
                },
            }),
        [colors.border, fonts.bodyMedium, fonts.bodyLarge, gridSize, roundness],
    );
};

export default ActionButton;
